import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Play,
  Users as UsersIcon,
  Award,
  Target as TargetIcon,
  CheckCircle,
  Target,
  BarChart3
} from "lucide-react";

import { setPreferredFormatLearning, setSupportRequired, setSkillsGapCount, setSkillsGap } from "../redux/userSlice";
import { updateUserProfilePreferences } from "../services/profileService";
import { skillGapAnalysisService } from "../services/skillGapAnalysisService";
import { COLORS, HEADING_COLOR } from "../constants/colors";

const learningFormats = [
  {
    id: "self-paced",
    title: "Self-Paced",
    description: "Flexible, Learn Anytime",
    icon: <Play className="w-5 h-5" />
  },
  {
    id: "instructor-led",
    title: "Instructor-Led",
    description: "Structured learning with expert guidance",
    icon: <UsersIcon className="w-5 h-5" />
  },
  {
    id: "certificate-programs",
    title: "Certificate Programs",
    description: "Formal certification and credentials",
    icon: <Award className="w-5 h-5" />
  },
  {
    id: "live-project-based",
    title: "Live / Project-Based",
    description: "Real-Time & In-Person/Hybrid",
    icon: <TargetIcon className="w-5 h-5" />
  }
];

const supportOptions = [
  {
    id: "self-directed",
    title: "Self Directed",
    description: "Learn independently at your own pace"
  },
  {
    id: "occasional-checkin",
    title: "Occasional Check-in",
    description: "Periodic guidance and progress reviews"
  },
  {
    id: "regular-coaching",
    title: "Regular Coaching",
    description: "Structured weekly coaching sessions"
  },
  {
    id: "intensive-support",
    title: "Intensive Support",
    description: "Daily support and close mentorship"
  },
  {
    id: "community-support",
    title: "Community Support",
    description: "Peer learning and group activities"
  },
  {
    id: "learning-buddy",
    title: "Learning Buddy",
    description: "Paired learning with a study partner"
  }
];

const CheckboxCard = ({ item, isChecked, onChange, icon = null }) => (
  <label className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 border border-gray-400 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
    <input
      type="checkbox"
      checked={isChecked}
      onChange={onChange}
      className="w-4 h-4 mt-1 sm:mt-2 text-purple-600 border-gray-400 rounded focus:ring-purple-500 flex-shrink-0"
    />
    <div className="flex flex-col gap-1 flex-1 min-w-0">
      <p className="font-medium text-sm sm:text-base text-gray-900 flex items-center gap-1">
        {icon} {item.title}
      </p>
      <p className="text-xs sm:text-sm text-gray-600">{item.description}</p>
    </div>
  </label>
);

const UpskillingPathwayConfig = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id: userId } = useSelector(state => state.auth);
  const { currentJob, targetJob, totalScore, preferredFormatLearning, supportRequired, skillsGapCount, skillsGap } = useSelector(state => state.user);
  const [selectedLearningFormats, setSelectedLearningFormats] = useState([]);
  const [selectedSupportOptions, setSelectedSupportOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false);

  useEffect(() => {
    if (preferredFormatLearning) setSelectedLearningFormats(preferredFormatLearning);
    if (supportRequired) setSelectedSupportOptions(supportRequired);
  }, [preferredFormatLearning, supportRequired]);

  useEffect(() => {
    const fetchLearningPlanAnalysis = async () => {
      if (!userId) return;
      
      if (skillsGapCount > 0 || skillsGap.length > 0) {
        return;
      }
      
      setIsLoadingAnalysis(true);
      try {
        const response = await skillGapAnalysisService.getLatestLearningPlanAnalysis();
        if (response.success) {
          dispatch(setSkillsGapCount(response.data.skillsGapCount));
          dispatch(setSkillsGap(response.data.skillsGap || []));
        }
      } catch (error) {
        console.error("Error fetching learning plan analysis:", error);
      } finally {
        setIsLoadingAnalysis(false);
      }
    };

    fetchLearningPlanAnalysis();
  }, [userId, dispatch, skillsGapCount, skillsGap.length]);

  const toggleSelection = (id, setter, selected) => {
    setter(selected.includes(id) ? selected.filter(item => item !== id) : [...selected, id]);
  };

  const handleGeneratePathway = async () => {
    if (!userId) return console.error("User ID not found");
    
    if (selectedLearningFormats.length === 0) {
      alert("Please select at least one learning format before generating your pathway.");
      return;
    }
    
    setIsLoading(true);

    try {
      dispatch(setPreferredFormatLearning(selectedLearningFormats));
      dispatch(setSupportRequired(selectedSupportOptions));
      await updateUserProfilePreferences(userId, {
        preferredFormatLearning: selectedLearningFormats,
        supportRequired: selectedSupportOptions
      });
      navigate("/dashboard/skills-and-learning/my-upskilling-pathway");
    } catch (error) {
      console.error("Error saving preferences:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-6 px-4 lg:px-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <section className="bg-white rounded-xl p-6 shadow-sm border border-gray-400">
          <div className="flex items-center gap-3 mb-2">
            <BarChart3 className="w-6 h-6 text-purple-600" />
            <h2 className="text-xl font-bold text-gray-900">
              Inputs to Generate your Upskilling Pathway
            </h2>
          </div>
          <p className="text-sm text-gray-600 mb-6">
            Track your completed assessments to get a more personalized pathway
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[
              { icon: CheckCircle, title: "Current Occupation", status: currentJob || "Not selected", color: currentJob ? "purple" : "gray" },
              { icon: CheckCircle, title: "Selected Target Career for Upskilling", status: targetJob || "Not selected", color: targetJob ? "purple" : "gray" },
              { 
                icon: Target, 
                title: "Skills Gap Analysis Results", 
                status: isLoadingAnalysis 
                  ? "Loading..." 
                  : skillsGapCount > 0 
                    ? `Count: ${skillsGapCount}` 
                    : "Not completed", 
                color: skillsGapCount > 0 ? "purple" : "gray" 
              },
              { icon: CheckCircle, title: "AI Literacy Quiz for the Target Occupation", status: totalScore ? `Score: ${totalScore}` : "Not completed", color: totalScore ? "purple" : "gray" },
            ].map(({ icon: Icon, title, status, color }, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-${color}-100`}>
                  <Icon className={`w-5 h-5 text-${color}-600`} />
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-base text-gray-900">{title}</p>
                  <p className={`text-sm text-${color}-600`}>{status}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white rounded-xl p-6 shadow-sm border border-gray-400">
                <h2 className="text-2xl font-bold mb-1" style={{ color: HEADING_COLOR }}>Learning Preferences</h2>
          <p className="text-sm text-gray-500 mb-6">
            Configure your learning format and support preferences
          </p>

          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Learning Formats</h3>
            <p className="text-sm text-gray-600 mb-4">You can select multiple learning formats</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {learningFormats.map(format => (
                <CheckboxCard
                  key={format.id}
                  item={format}
                  icon={format.icon}
                  isChecked={selectedLearningFormats.includes(format.id)}
                  onChange={() => toggleSelection(format.id, setSelectedLearningFormats, selectedLearningFormats)}
                />
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Support Required</h3>
            <p className="text-sm text-gray-600 mb-4">You can select multiple support options</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {supportOptions.map(option => (
                <CheckboxCard
                  key={option.id}
                  item={option}
                  isChecked={selectedSupportOptions.includes(option.id)}
                  onChange={() => toggleSelection(option.id, setSelectedSupportOptions, selectedSupportOptions)}
                />
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white rounded-xl p-6 shadow-sm border border-gray-400">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Generate Your Pathway</h2>
          <p className="text-sm text-gray-600 mb-6">
            Ready to create your personalized upskilling plan
          </p>
          
          {selectedLearningFormats.length === 0 && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                ⚠️ Please select at least one learning format above to generate your pathway.
              </p>
            </div>
          )}
          
          <button
            onClick={handleGeneratePathway}
            disabled={isLoading || selectedLearningFormats.length === 0}
            className="w-full text-black border border-gray-400 py-2 px-4 rounded-lg transition-colors font-medium flex items-center justify-center gap-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 hover:bg-gray-200"
          >
            <TargetIcon className="w-6 h-6" />
            {isLoading ? "Generating..." : (
              <>
                <span className="hidden sm:inline">Generate My Upskilling Pathway</span>
                <span className="sm:hidden">Generate Pathway</span>
              </>
            )}
          </button>
        </section>
      </div>
    </div>
  );
};

export default UpskillingPathwayConfig;
